import { Message, Sender } from '../types/tutor';
import { HISTORY_MAX_LENGTH } from '../constants/tutor';

const STREAM_TIMEOUT_MS = 60_000;      // 60s overall timeout
const CHUNK_TIMEOUT_MS = 30_000;       // 30s max silence between chunks (Gemini 2.5 "thinking" can be silent for 15-25s)

export async function* sendMessageToGeminiStream(
    history: Message[],
    newMessage: string,
    emojiLevel: 'none' | 'medium' | 'many' = 'medium',
    contextBlock?: string,
    abortSignal?: AbortSignal,
    userToken?: string
): AsyncGenerator<string, void, unknown> {
    // Trim history to prevent unbounded context growth
    const trimmedHistory = history
        .filter(m => m.sender !== Sender.SYSTEM)
        .filter(m => m.id !== 'init-1');

    const recentHistory = trimmedHistory.slice(-HISTORY_MAX_LENGTH);

    const contents = recentHistory.map(m => ({
        role: m.sender === Sender.USER ? 'user' : 'model',
        // Strip pill markup from history — it wastes context tokens and isn't needed by the model
        parts: [{ text: m.text.replace(/\|\|\|PILL:.*?\|\|\|/g, '').trim() }]
    }));

    // Prepend context to newMessage if provided, with focus reminder
    const userPrompt = contextBlock
        ? `${contextBlock}\n\n${newMessage}\n\n[Odpovídej v kontextu probíhající konverzace a aktuální úlohy.]`
        : newMessage;

    const lastContent = contents.length > 0 ? contents[contents.length - 1] : null;

    // The newMessage is usually already at the end of history because TutorModule appends it to state first.
    // If the last message in contents is a user message, we update it rather than duplicating it.
    if (lastContent && lastContent.role === 'user') {
        lastContent.parts[0].text = userPrompt;
    } else {
        contents.push({
            role: 'user',
            parts: [{ text: userPrompt }]
        });
    }

    // AbortController for overall timeout + caller abort
    const controller = new AbortController();
    const overallTimer = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

    // If the caller provides an abort signal (e.g. component unmount), chain it
    if (abortSignal) {
        if (abortSignal.aborted) {
            clearTimeout(overallTimer);
            controller.abort();
        } else {
            abortSignal.addEventListener('abort', () => controller.abort(), { once: true });
        }
    }

    try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (userToken) {
            headers['Authorization'] = `Bearer ${userToken}`;
        }

        const response = await fetch('/.netlify/functions/gemini-chat', {
            method: 'POST',
            headers,
            body: JSON.stringify({ contents, emojiLevel }),
            signal: controller.signal
        });

        if (!response.ok) {
            let errorMessage = "Omlouváme se, došlo k neočekávané chybě.";
            try {
                const errorData = await response.json();
                if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } catch {
                // If JSON parsing fails, use default message
            }
            throw new Error(errorMessage);
        }

        if (!response.body) throw new Error("No response body");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        try {
            while (true) {
                // Per-chunk inactivity timeout
                const chunkResult = await Promise.race([
                    reader.read(),
                    new Promise<never>((_, reject) => {
                        const chunkTimer = setTimeout(
                            () => reject(new Error('Stream stalled — no data received for 15 seconds.')),
                            CHUNK_TIMEOUT_MS
                        );
                        // Clean up timer if the controller aborts
                        controller.signal.addEventListener('abort', () => clearTimeout(chunkTimer), { once: true });
                    })
                ]);

                const { done, value } = chunkResult;
                if (done) break;
                yield decoder.decode(value, { stream: true });
            }
        } finally {
            reader.releaseLock();
        }
    } finally {
        clearTimeout(overallTimer);
    }
}

/**
 * Simple passthrough parser with cleanup for leaked "Thoughts".
 * Sometimes the model leaks "THINK" blocks despite instructions. We strip them here.
 */
export const STREAM_COMPLETE_SENTINEL = '%%STREAM_COMPLETE%%';

/**
 * Simple passthrough parser with cleanup for leaked "Thoughts" and stream sentinel.
 * Sometimes the model leaks "THINK" blocks despite instructions. We strip them here.
 */
export function parseResponse(rawText: string): { tutorResponse: string, internalAnalysis: string, isComplete: boolean } {
    const isComplete = rawText.includes(STREAM_COMPLETE_SENTINEL);
    let cleanText = rawText.replace(STREAM_COMPLETE_SENTINEL, '').trim();

    // Remove "THINK" blocks if they appear at the start (common model leak)
    if (cleanText.startsWith("THINK")) {
        const match = cleanText.match(/^THINK[\s\S]*?(\n\n|$)/);
        if (match) {
            cleanText = cleanText.replace(/^THINK[\s\S]*?(\n\n|$)/, "").trim();
        }
    }

    return { tutorResponse: cleanText, internalAnalysis: '', isComplete };
}
